/* eslint-disable import/no-unused-modules */
import { type BigNumber } from "ethers"
import { useMemo } from "react"

import useLocalStorage from "common/hooks/useLocalStorage"
import { isAttemptExpired } from "common/helpers/date"
import { getAccountIdParams } from "web3/helpers/accountId"
import { getChainByChainId } from "web3/helpers/findChain"

type ActionType = "wrap" | "unwrap" | "approve"

type TokenWrapperAttempt = {
  txHash?: string
  createdAt: string
  amount: BigNumber
  actionType: ActionType
  optimisticBalance?: number
}

type Values = {
  tokenWrapperAttempt: TokenWrapperAttempt | undefined
}

export const useTokenWrapperAttemptQuery = ({
  address,
  tokenId,
}: {
  address: string
  tokenId: string
}): Values => {
  const [tokenWrapperAttemptStorage, setTokenWrapperAttemptStorage] =
    useLocalStorage<TokenWrapperAttempt | undefined>(
      getTokenWrapperKey(address, tokenId),
      undefined,
    )

  const tokenWrapperAttempt = useMemo(() => {
    if (!tokenId) {
      return undefined
    }
    const { chainId } = getAccountIdParams(tokenId)
    const foundChain = chainId ? getChainByChainId(chainId) : undefined
    const expireTimeInMins = foundChain ? (foundChain.blockTime * 10) / 60 : 2

    if (tokenWrapperAttemptStorage) {
      if (
        !isAttemptExpired(
          tokenWrapperAttemptStorage.createdAt,
          expireTimeInMins,
        )
      ) {
        return tokenWrapperAttemptStorage
      } else {
        setTokenWrapperAttemptStorage(undefined)

        return undefined
      }
    }

    return undefined
  }, [setTokenWrapperAttemptStorage, tokenId, tokenWrapperAttemptStorage])

  return {
    tokenWrapperAttempt,
  }
}

export const useTokenWrapperAttemptMutation = () => {
  const mutate = ({
    txHash,
    address,
    tokenId,
    amount,
    actionType,
    optimisticBalance,
  }: {
    txHash: string
    address: string
    tokenId: string
    amount: BigNumber
    actionType: ActionType
    optimisticBalance?: number
  }) => {
    const key = getTokenWrapperKey(address, tokenId)
    const newAttempt: TokenWrapperAttempt = {
      txHash,
      createdAt: new Date().toISOString(),
      amount,
      actionType,
      optimisticBalance,
    }
    window.localStorage.setItem(key, JSON.stringify(newAttempt))
  }

  return {
    mutate,
  }
}

const getTokenWrapperKey = (address: string, tokenId: string) => {
  return `${address}-wrap-${tokenId}`
}
